import Phaser from 'phaser';
import {
    TILE_SIZE,
    CHUNK_SIZE,
    CHUNKS_LOADED,
    MOVE_INTERVAL,
    JUMPSQUAT_DURATION,
    MIN_JUMP_TILES,
    PlayerState,
    TileType,
    TILE_COLORS,
    ChunkData,
} from '@blockworld/shared';
import { NetworkClient, PlayerSnapshot } from '../network/NetworkClient';
import { TouchControls } from '../input/TouchControls';

// ============================================================
// POSITION QUEUE FOR OTHER PLAYERS
// - Immediate updates (no buffer delay)
// - forcePosition for block-action sync (with time-based rejection)
// - Falling prediction on global tick (like falling blocks)
// - Air strafe applied immediately
// ============================================================

class PositionQueue {
    private queue: PlayerSnapshot[] = [];
    private currentPosition: PlayerSnapshot | null = null;
    private readonly MAX_QUEUE_SIZE = 30;
    private lastPredictedTick = 0;
    private forcedUntilTime = 0; // Reject positions until this time passes

    push(snapshot: PlayerSnapshot): void {
        const now = Date.now();
        
        // Reject positions that arrive within 200ms of a force
        // This prevents stale network data from overwriting synced position
        if (now < this.forcedUntilTime) {
            return;
        }
        
        // Reject positions that would teleport a falling player backwards (up)
        // This prevents rubber-banding from out-of-order network data
        if (this.currentPosition && 
            this.currentPosition.state === PlayerState.FALLING &&
            snapshot.y < this.currentPosition.y) {
            // Stale position - player is falling, can't go up
            return;
        }
        
        // Insert in sequence order
        let inserted = false;
        for (let i = this.queue.length - 1; i >= 0; i--) {
            if (this.queue[i].sequence < snapshot.sequence) {
                this.queue.splice(i + 1, 0, snapshot);
                inserted = true;
                break;
            }
        }
        if (!inserted) {
            this.queue.unshift(snapshot);
        }
        while (this.queue.length > this.MAX_QUEUE_SIZE) {
            this.queue.shift();
        }
        
        // Apply immediately to current position (no queuing delay)
        if (this.queue.length > 0) {
            this.currentPosition = this.queue[this.queue.length - 1];
            // Reset prediction tick when we get authoritative data
            this.lastPredictedTick = Math.floor(now / 100);
        }
    }

    // Force immediate position update (for block action sync)
    // Rejects positions for a short window to prevent stale overwrites
    forcePosition(snapshot: PlayerSnapshot): void {
        this.queue = [];
        this.currentPosition = snapshot;
        // Reject positions for next 200ms (time for old data to flush)
        this.forcedUntilTime = Date.now() + 200;
        // Reset prediction tick so we don't double-fall
        this.lastPredictedTick = Math.floor(Date.now() / 100);
    }

    // Called on global tick - predict falling (with ground check)
    stepOnTick(globalTick: number, canFall: (x: number, y: number, isCrouching: boolean) => boolean): void {
        // Predict falling: if player is FALLING, move them down 1 tile per tick
        // This syncs remote falling players with falling blocks
        if (this.currentPosition && 
            this.currentPosition.state === PlayerState.FALLING &&
            globalTick > this.lastPredictedTick) {
            
            // Check if there's ground below before predicting fall
            if (canFall(this.currentPosition.x, this.currentPosition.y, this.currentPosition.isCrouching)) {
                this.lastPredictedTick = globalTick;
                // Predict fall
                this.currentPosition = {
                    ...this.currentPosition,
                    y: this.currentPosition.y + 1
                };
            } else {
                // Hit ground - stop falling, change to grounded state
                this.currentPosition = {
                    ...this.currentPosition,
                    state: this.currentPosition.isCrouching ? PlayerState.CROUCHING : PlayerState.IDLE
                };
            }
        }
    }
    
    // Apply immediate air strafe (horizontal movement while falling/rising)
    applyAirStrafe(dir: number): void {
        if (this.currentPosition) {
            this.currentPosition = {
                ...this.currentPosition,
                x: this.currentPosition.x + dir,
                facing: dir
            };
        }
    }

    // Get current discrete position
    getCurrentPosition(): PlayerSnapshot | null {
        return this.currentPosition;
    }
    
    // Legacy method for compatibility
    getInterpolatedPosition(now: number): PlayerSnapshot | null {
        return this.currentPosition;
    }
}

// ============================================================
// LOCAL PLAYER STATE
// ============================================================

interface LocalPlayerState {
    x: number;
    y: number;
    state: PlayerState;
    facing: number;
    isCrouching: boolean;
    isHoldingBlock: boolean;
    heldBlockType: TileType | null;
    jumpRemaining: number;
    minJumpRemaining: number;
    jumpsquatStartTime: number;
    risingStartTime: number; // When RISING state started (for stuck detection)
    lastRisingY: number; // Last Y during RISING (to detect stuck)
    pendingLedgeDir: number; // -1, 0, or 1 - direction queued for walk-off-ledge
    pendingBlockPlace: boolean; // true if block place queued for next global tick
    pendingBlockPickup: boolean; // true if block pickup queued for next global tick
    // Captured coordinates at input time (for falling actions)
    pendingActionX: number;
    pendingActionY: number;
}

// ============================================================
// GAME SCENE
// ============================================================

export class GameScene extends Phaser.Scene {
    private networkClient: NetworkClient;
    private touchControls!: TouchControls;

    // World data
    private chunks: Map<string, number[][]> = new Map();
    private worldSeed: number = 0;

    // Sprites
    private localPlayerSprite!: Phaser.GameObjects.Sprite;
    private localHeldBlockSprite!: Phaser.GameObjects.Image;
    private otherPlayerSprites: Map<string, Phaser.GameObjects.Sprite> = new Map();
    private otherPlayerHeldBlocks: Map<string, Phaser.GameObjects.Image> = new Map();
    
    // Cache of hue-shifted textures per player ID
    private playerTextureCache: Map<string, {
        man_right: string;
        squat_man_right: string;
        leap_man_right: string;
        fall_man_right: string;
    }> = new Map();
    
    // Processed texture keys (with black transparency)
    private processedTextures!: {
        man_right: string;
        squat_man_right: string;
        leap_man_right: string;
        fall_man_right: string;
        dirt: string;
        stone: string;
        grass: string;
    };
    
    // Tile sprites pool (keyed by screen position for reuse)
    private tileSprites: Map<string, Phaser.GameObjects.Image> = new Map();
    private activeTileKeys: Set<string> = new Set();

    // Input
    private cursors!: Phaser.Types.Input.Keyboard.CursorKeys;
    private inputState = {
        left: false,
        right: false,
        up: false,
        down: false,
        downJustPressed: false,
    };
    private lastDownState = false;
    private lastLeftState = false;
    private lastRightState = false;
    private horizontalJustPressed = false;

    // Local player
    private localPlayer: LocalPlayerState | null = null;
    private localPlayerId: string | null = null;

    // UNIFIED movement timing
    private lastUnifiedMoveTime = 0;
    private lastHorizontalMoveTime = 0;
    private skipNextTickHorizontal = false; // Set by instant move to skip next global tick's horizontal
    
    // Global aligned fall tick (syncs with server block falls)
    private lastGlobalFallTick = 0;

    private lastPositionSendTime = 0;

    // Other players
    private otherPlayers: Map<string, PositionQueue> = new Map();

    // Chunks
    private subscribedChunks: Set<string> = new Set();
    private lastChunkCenter: string = '';
    
    // Client-side falling block prediction (synced with player fall tick)
    private localFallingBlocks: Set<string> = new Set();
    
    // Queue for server block updates (processed on global tick to sync timing)
    private pendingServerBlockUpdates: Array<{x: number, y: number, tileType: number, action: string}> = [];

    constructor(networkClient: NetworkClient) {
        super({ key: 'GameScene' });
        this.networkClient = networkClient;
    }

    preload(): void {
        // Load player sprites (BMP to avoid PNG compression artifacts)
        this.load.image('man_right', 'sprites/man_right.bmp');
        this.load.image('squat_man_right', 'sprites/squat_man_right.bmp');
        this.load.image('leap_man_right', 'sprites/leap_man_right.bmp');
        this.load.image('fall_man_right', 'sprites/fall_man_right.bmp');
        
        // Load tile sprites
        this.load.image('dirt', 'sprites/dirt.png');
        this.load.image('stone', 'sprites/stone.png');
        this.load.image('grass', 'sprites/grass.png');
    }
    
    // Process a texture to make pure black (0x000000) pixels transparent
    // Returns the new texture key
    private makeBlackTransparent(textureKey: string): string {
        const texture = this.textures.get(textureKey);
        if (!texture) {
            console.warn(`Texture ${textureKey} not found`);
            return textureKey;
        }
        const source = texture.getSourceImage() as HTMLImageElement;
        
        // Create a canvas to manipulate the image
        const canvas = document.createElement('canvas');
        canvas.width = source.width;
        canvas.height = source.height;
        const ctx = canvas.getContext('2d')!;
        ctx.drawImage(source, 0, 0);
        
        // Get image data and process pixels
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        
        // Threshold for "black" - allow near-black colors too
        // BMP files may save black as (1,0,0) or similar
        const threshold = 10;
        let transparentCount = 0;
        
        for (let i = 0; i < data.length; i += 4) {
            // Check if pixel is black or near-black
            if (data[i] <= threshold && data[i + 1] <= threshold && data[i + 2] <= threshold) {
                // Make it transparent
                data[i + 3] = 0;
                transparentCount++;
            }
        }
        
        console.log(`${textureKey}: made ${transparentCount} pixels transparent out of ${data.length / 4}`);
        
        ctx.putImageData(imageData, 0, 0);
        
        // Add the processed texture with a new key
        const processedKey = textureKey + '_processed';
        this.textures.addCanvas(processedKey, canvas);
        return processedKey;
    }
    
    // RGB to HSL conversion
    private rgbToHsl(r: number, g: number, b: number): [number, number, number] {
        r /= 255; g /= 255; b /= 255;
        const max = Math.max(r, g, b), min = Math.min(r, g, b);
        let h = 0, s = 0;
        const l = (max + min) / 2;

        if (max !== min) {
            const d = max - min;
            s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
            switch (max) {
                case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
                case g: h = ((b - r) / d + 2) / 6; break;
                case b: h = ((r - g) / d + 4) / 6; break;
            }
        }
        return [h, s, l];
    }
    
    // HSL to RGB conversion
    private hslToRgb(h: number, s: number, l: number): [number, number, number] {
        let r, g, b;
        if (s === 0) {
            r = g = b = l;
        } else {
            const hue2rgb = (p: number, q: number, t: number) => {
                if (t < 0) t += 1;
                if (t > 1) t -= 1;
                if (t < 1/6) return p + (q - p) * 6 * t;
                if (t < 1/2) return q;
                if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
                return p;
            };
            const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
            const p = 2 * l - q;
            r = hue2rgb(p, q, h + 1/3);
            g = hue2rgb(p, q, h);
            b = hue2rgb(p, q, h - 1/3);
        }
        return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
    }
    
    // Create a hue-shifted version of a texture (only shifts saturated pixels)
    // hueShift is 0-1 (fraction of full rotation)
    private createHueShiftedTexture(baseTextureKey: string, hueShift: number, newKey: string): string {
        const texture = this.textures.get(baseTextureKey);
        if (!texture) {
            console.warn(`Texture ${baseTextureKey} not found for hue shift`);
            return baseTextureKey;
        }
        
        // Get the canvas from the processed texture
        const source = texture.getSourceImage() as HTMLCanvasElement;
        
        const canvas = document.createElement('canvas');
        canvas.width = source.width;
        canvas.height = source.height;
        const ctx = canvas.getContext('2d')!;
        ctx.drawImage(source, 0, 0);
        
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        
        // Saturation threshold - pixels with saturation below this are considered greyscale
        const saturationThreshold = 0.15;
        
        for (let i = 0; i < data.length; i += 4) {
            const r = data[i];
            const g = data[i + 1];
            const b = data[i + 2];
            const a = data[i + 3];
            
            // Skip transparent pixels
            if (a === 0) continue;
            
            const [h, s, l] = this.rgbToHsl(r, g, b);
            
            // Only shift hue for saturated pixels (not greyscale)
            if (s > saturationThreshold) {
                const newH = (h + hueShift) % 1;
                const [newR, newG, newB] = this.hslToRgb(newH, s, l);
                data[i] = newR;
                data[i + 1] = newG;
                data[i + 2] = newB;
            }
            // Greyscale pixels (low saturation) remain unchanged
        }
        
        ctx.putImageData(imageData, 0, 0);
        this.textures.addCanvas(newKey, canvas);
        return newKey;
    }
    
    // Get a deterministic hue shift for a player based on their ID
    private getPlayerHueShift(playerId: string): number {
        // Hash the player ID to get a consistent hue shift
        let hash = 0;
        for (let i = 0; i < playerId.length; i++) {
            const char = playerId.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash; // Convert to 32-bit integer
        }
        // Convert to 0-1 range, avoiding values too close to 0 (original color)
        // Use golden ratio for good distribution
        const goldenRatio = 0.618033988749895;
        return ((Math.abs(hash) * goldenRatio) % 1);
    }
    
    // Get or create hue-shifted textures for a player
    private getPlayerTextures(playerId: string): {
        man_right: string;
        squat_man_right: string;
        leap_man_right: string;
        fall_man_right: string;
    } {
        // Check if we already created textures for this player
        if (this.playerTextureCache.has(playerId)) {
            return this.playerTextureCache.get(playerId)!;
        }
        
        const hueShift = this.getPlayerHueShift(playerId);
        const suffix = `_player_${playerId}`;
        
        const textures = {
            man_right: this.createHueShiftedTexture(this.processedTextures.man_right, hueShift, `man_right${suffix}`),
            squat_man_right: this.createHueShiftedTexture(this.processedTextures.squat_man_right, hueShift, `squat_man_right${suffix}`),
            leap_man_right: this.createHueShiftedTexture(this.processedTextures.leap_man_right, hueShift, `leap_man_right${suffix}`),
            fall_man_right: this.createHueShiftedTexture(this.processedTextures.fall_man_right, hueShift, `fall_man_right${suffix}`),
        };
        
        this.playerTextureCache.set(playerId, textures);
        return textures;
    }

    create(): void {
        // Set background to sky color (behind everything, including crouching players)
        this.cameras.main.setBackgroundColor(0x87CEEB);
        
        // Process sprites to make black (0x000000) transparent
        // Store processed keys for use throughout the game
        this.processedTextures = {
            man_right: this.makeBlackTransparent('man_right'),
            squat_man_right: this.makeBlackTransparent('squat_man_right'),
            leap_man_right: this.makeBlackTransparent('leap_man_right'),
            fall_man_right: this.makeBlackTransparent('fall_man_right'),
            dirt: this.makeBlackTransparent('dirt'),
            stone: this.makeBlackTransparent('stone'),
            grass: this.makeBlackTransparent('grass'),
        };
        
        // Depth layers:
        // Background = sky color (behind everything)
        // Crouching players at depth 5 (behind block tiles, above sky)
        // Block tiles at depth 10
        // Other players at depth 50
        // Other players' held blocks at depth 55
        // Standing local player at depth 100
        // Local held block at depth 110

        // Create local player sprite (hidden until we have position)
        this.localPlayerSprite = this.add.sprite(0, 0, this.processedTextures.man_right);
        this.localPlayerSprite.setOrigin(0, 0);
        this.localPlayerSprite.setScale(1);
        this.localPlayerSprite.setVisible(false);
        this.localPlayerSprite.setDepth(100);
        
        // Create local held block sprite (hidden until holding)
        this.localHeldBlockSprite = this.add.image(0, 0, this.processedTextures.dirt);
        this.localHeldBlockSprite.setOrigin(0, 0);
        this.localHeldBlockSprite.setDepth(110);
        this.localHeldBlockSprite.setVisible(false);

        // Input
        this.cursors = this.input.keyboard!.createCursorKeys();
        this.touchControls = new TouchControls(this);
        this.touchControls.create();

        // Network callbacks
        this.setupNetworkCallbacks();
    }

    private setupNetworkCallbacks(): void {
        this.networkClient.onInitialState = (localId, seed, players) => {
            this.localPlayerId = localId;
            this.worldSeed = seed;

            for (const p of players) {
                if (p.id !== localId) {
                    const queue = new PositionQueue();
                    queue.push(p);
                    this.otherPlayers.set(p.id, queue);
                } else {
                    this.localPlayer = {
                        x: p.x,
                        y: p.y,
                        state: p.state,
                        facing: p.facing,
                        isCrouching: p.isCrouching,
                        isHoldingBlock: p.isHoldingBlock,
                        heldBlockType: p.heldBlockType >= 0 ? p.heldBlockType : null,
                        jumpRemaining: 0,
                        minJumpRemaining: 0,
                        jumpsquatStartTime: 0,
                        risingStartTime: 0,
                        lastRisingY: 0,
                        pendingLedgeDir: 0,
                        pendingBlockPlace: false,
                        pendingBlockPickup: false,
                        pendingActionX: 0,
                        pendingActionY: 0,
                    };
                    this.localPlayerSprite.setVisible(true);
                }
            }
        };

        this.networkClient.onPlayerPositions = (snapshots) => {
            const now = Date.now();
            for (const snap of snapshots) {
                if (snap.id === this.localPlayerId) continue;

                let queue = this.otherPlayers.get(snap.id);
                if (!queue) {
                    queue = new PositionQueue();
                    this.otherPlayers.set(snap.id, queue);
                }
                queue.push({ ...snap, timestamp: now });
            }
        };

        this.networkClient.onPlayerJoin = (player) => {
            if (player.id === this.localPlayerId) return;
            const queue = new PositionQueue();
            queue.push({ ...player, timestamp: Date.now() });
            this.otherPlayers.set(player.id, queue);
        };

        this.networkClient.onPlayerLeave = (playerId) => {
            this.otherPlayers.delete(playerId);
            const sprite = this.otherPlayerSprites.get(playerId);
            if (sprite) {
                sprite.destroy();
                this.otherPlayerSprites.delete(playerId);
            }
            const heldBlockGfx = this.otherPlayerHeldBlocks.get(playerId);
            if (heldBlockGfx) {
                heldBlockGfx.destroy();
                this.otherPlayerHeldBlocks.delete(playerId);
            }
        };

        this.networkClient.onChunkData = (chunks) => {
            for (const chunk of chunks) {
                const key = `${chunk.cx},${chunk.cy}`;
                this.chunks.set(key, chunk.tiles);
            }
        };

        this.networkClient.onBlockUpdate = (updates) => {
            for (const update of updates) {
                // If block update includes player position, apply BOTH immediately
                // This ensures block and player appear at same moment
                const hasPlayerSync = update.playerId && 
                                      update.playerId !== this.localPlayerId && 
                                      update.playerX !== undefined && 
                                      update.playerY !== undefined;
                
                if (hasPlayerSync) {
                    const playerId = update.playerId!;
                    const playerX = update.playerX!;
                    const playerY = update.playerY!;
                    
                    // Apply block immediately (no queue)
                    const cx = Math.floor(update.x / CHUNK_SIZE);
                    const cy = Math.floor(update.y / CHUNK_SIZE);
                    const chunkKey = `${cx},${cy}`;
                    const chunk = this.chunks.get(chunkKey);
                    if (chunk) {
                        const localX = ((update.x % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
                        const localY = ((update.y % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
                        chunk[localY][localX] = update.tileType;
                    }
                    
                    const key = `${update.x},${update.y}`;
                    // Add to falling list if it's a falling type (dirt or grass)
                    if (update.action === 'place' && (update.tileType === TileType.DIRT || update.tileType === TileType.GRASS)) {
                        this.localFallingBlocks.add(key);
                    } else if (update.action === 'pickup') {
                        this.localFallingBlocks.delete(key);
                    }
                    
                    // Force player position immediately
                    let queue = this.otherPlayers.get(playerId);
                    if (!queue) {
                        queue = new PositionQueue();
                        this.otherPlayers.set(playerId, queue);
                    }
                    queue.forcePosition({
                        id: playerId,
                        x: playerX,
                        y: playerY,
                        state: update.playerState ?? PlayerState.IDLE,
                        facing: update.playerFacing ?? 1,
                        isCrouching: update.playerIsCrouching ?? false,
                        isHoldingBlock: update.playerIsHoldingBlock ?? false,
                        heldBlockType: update.playerHeldBlockType ?? -1,
                        timestamp: Date.now(),
                        sequence: Date.now(),
                    });
                } else {
                    // No player sync - queue for global tick (local player's own blocks)
                    this.pendingServerBlockUpdates.push({
                        x: update.x,
                        y: update.y,
                        tileType: update.tileType,
                        action: update.action
                    });
                }
            }
        };

        // Handle rollback from server (race condition resolution)
        this.networkClient.onBlockActionRollback = (data) => {
            if (!this.localPlayer) return;
            
            console.warn('Block action rolled back:', data);
            
            if (data.action === 'pickup' && data.reason === 'block_not_found') {
                // We tried to pick up a block that wasn't there
                // Could be: someone else got it, or it's at different Y due to fall timing
                // Take away the block we thought we picked up
                if (this.localPlayer.isHoldingBlock) {
                    this.localPlayer.isHoldingBlock = false;
                    this.localPlayer.heldBlockType = null;
                }
                // DON'T set tile to SKY - block might be at Y+1 or Y-1
                // Server broadcasts will correct world state
            } else if (data.action === 'place' && data.reason === 'spot_occupied') {
                // We tried to place a block where something already exists
                // Give us back the block we thought we placed
                if (!this.localPlayer.isHoldingBlock && data.blockType !== undefined) {
                    this.localPlayer.isHoldingBlock = true;
                    this.localPlayer.heldBlockType = data.blockType;
                }
                // Remove our optimistically placed block
                this.setLocalTile(data.x, data.y, TileType.SKY);
            }
        };
        
        // Handle air strafe from other players (immediate horizontal movement)
        this.networkClient.onAirStrafe = (playerId, dir) => {
            const queue = this.otherPlayers.get(playerId);
            if (queue) {
                queue.applyAirStrafe(dir);
            }
        };
    }

    update(time: number, delta: number): void {
        const now = Date.now();
        this.updateInput();
        this.updateLocalPlayer(now);
        this.sendPositionUpdate(now);
        this.updateChunkSubscriptions();
        this.render(now);
    }

    // ============================================================
    // INPUT
    // ============================================================

    private updateInput(): void {
        const touch = this.touchControls.state;

        const currentDown = this.cursors.down.isDown || touch.down;
        const currentLeft = this.cursors.left.isDown || touch.left;
        const currentRight = this.cursors.right.isDown || touch.right;

        this.inputState.downJustPressed = (currentDown && !this.lastDownState) || touch.downJustPressed;

        // Track horizontal just pressed
        const leftJustPressed = currentLeft && !this.lastLeftState;
        const rightJustPressed = currentRight && !this.lastRightState;
        this.horizontalJustPressed = leftJustPressed || rightJustPressed;

        this.lastDownState = currentDown;
        this.lastLeftState = currentLeft;
        this.lastRightState = currentRight;

        this.inputState.left = currentLeft;
        this.inputState.right = currentRight;
        this.inputState.up = this.cursors.up.isDown || touch.up;
        this.inputState.down = currentDown;

        this.touchControls.clearJustPressed();
    }

    // ============================================================
    // SYNCHRONIZED MOVEMENT SYSTEM
    // ============================================================

    private updateLocalPlayer(now: number): void {
        if (!this.localPlayer) return;
        const player = this.localPlayer;

        // Down action (instant)
        if (this.inputState.downJustPressed) {
            this.handleDownAction(player);
        }

        // Uncrouch (instant)
        if (!this.inputState.down && player.isCrouching) {
            this.tryEndCrouch(player);
        }

        // Jump initiation (instant)
        if (this.inputState.up && this.isOnGround(player) &&
            player.state !== PlayerState.JUMPSQUAT &&
            player.state !== PlayerState.RISING) {
            player.state = PlayerState.JUMPSQUAT;
            player.jumpsquatStartTime = now;
            player.jumpRemaining = 30;
            player.minJumpRemaining = MIN_JUMP_TILES;
            player.pendingLedgeDir = 0; // Clear any pending ledge - jump overrides it
        }

        // Jumpsquat timing - when complete, immediately do first rise
        if (player.state === PlayerState.JUMPSQUAT) {
            if (now - player.jumpsquatStartTime >= JUMPSQUAT_DURATION) {
                player.state = PlayerState.RISING;
                player.risingStartTime = now;
                player.lastRisingY = player.y;
                
                // Check if horizontal is buffered during jumpsquat
                const wantsHorizontalNow = (this.inputState.left && !this.inputState.right) ||
                                           (this.inputState.right && !this.inputState.left);
                
                // Try horizontal FIRST (step-up counts as vertical)
                let didStepUp = false;
                if (wantsHorizontalNow) {
                    const dir = this.inputState.left ? -1 : 1;
                    player.facing = dir;
                    const yBefore = player.y;
                    if (this.tryMoveHorizontal(player, dir)) {
                        this.lastHorizontalMoveTime = now;
                        didStepUp = player.y < yBefore;
                    }
                }
                
                // Do the first rise move (unless step-up already moved us up)
                if (!didStepUp) {
                    if (this.tryMoveUp(player)) {
                        player.jumpRemaining--;
                        if (player.minJumpRemaining > 0) player.minJumpRemaining--;
                    } else {
                        // Bonked head - immediately start falling
                        player.minJumpRemaining = 0;
                        player.state = PlayerState.FALLING;
                    }
                } else {
                    // Step-up counts as first vertical move - consume jump
                    player.jumpRemaining--;
                    if (player.minJumpRemaining > 0) player.minJumpRemaining--;
                }
                
                this.lastUnifiedMoveTime = now; // Subsequent rises follow tick timing
            }
            return;
        }

        // GLOBAL ALIGNED FALLING - check every frame, synced to server block ticks
        // This handles the VERTICAL fall movement only
        const globalTick = Math.floor(now / MOVE_INTERVAL);
        const globalTickChanged = globalTick > this.lastGlobalFallTick;
        
        const wantsHorizontal = (this.inputState.left && !this.inputState.right) ||
                               (this.inputState.right && !this.inputState.left);
        
        // Track if we were falling at start of frame (to prevent double-moves if we land)
        const wasFalling = player.state === PlayerState.FALLING;
        
        if (globalTickChanged) {
            this.lastGlobalFallTick = globalTick;
            
            // Ground check callback for remote player fall prediction
            const canFall = (x: number, y: number, isCrouching: boolean): boolean => {
                const h = isCrouching ? 1 : 2;
                // Can fall if there's sky below feet
                return this.getTile(x, y + h) === TileType.SKY;
            };
            
            // Step all other players on global tick (syncs their falls with blocks)
            for (const queue of this.otherPlayers.values()) {
                queue.stepOnTick(globalTick, canFall);
            }
            
            // Process pending server block updates immediately (no buffer delay)
            // Sync comes from processing on same global tick, not from delaying
            for (const update of this.pendingServerBlockUpdates) {
                // Apply tile change
                const cx = Math.floor(update.x / CHUNK_SIZE);
                const cy = Math.floor(update.y / CHUNK_SIZE);
                const chunkKey = `${cx},${cy}`;
                const chunk = this.chunks.get(chunkKey);
                if (chunk) {
                    const localX = ((update.x % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
                    const localY = ((update.y % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
                    chunk[localY][localX] = update.tileType;
                }
                
                const key = `${update.x},${update.y}`;
                
                // Track falling blocks based on action
                // Add to falling list if it's a falling type (dirt or grass)
                if (update.action === 'place' && (update.tileType === TileType.DIRT || update.tileType === TileType.GRASS)) {
                    // Always add placed falling types - tick logic determines stability
                    this.localFallingBlocks.add(key);
                } else if (update.action === 'pickup') {
                    // Block removed - stop tracking
                    this.localFallingBlocks.delete(key);
                }
            }
            this.pendingServerBlockUpdates = [];
            
            // Tick local falling blocks AFTER new blocks are added
            // (so newly placed blocks start falling on same tick)
            this.tickLocalFallingBlocks();
            
            // Process pending walk-off-ledge (diagonal move synced to global tick)
            if (player.state === PlayerState.FALLING && player.pendingLedgeDir !== 0) {
                const dir = player.pendingLedgeDir;
                player.pendingLedgeDir = 0; // Clear pending
                player.facing = dir;
                
                // Do the diagonal: horizontal + down
                player.x += dir;
                this.tryMoveDown(player);
                this.lastHorizontalMoveTime = now;
                this.lastUnifiedMoveTime = now;
                
                // Check if landed (unlikely but possible if there was ground 2 below)
                if (this.isOnGround(player)) {
                    player.state = player.isCrouching ? PlayerState.CROUCHING : PlayerState.IDLE;
                }
                // Skip regular falling - we already processed this tick's fall
            }
            // Regular falling (no pending ledge)
            else if (player.state === PlayerState.FALLING && !this.isOnGround(player)) {
                this.tryMoveDown(player);
                
                // HORIZONTAL AIR-STRAFE synced to fall tick (for perfect diagonal)
                // Skip if just pressed OR if instant move consumed this tick's horizontal
                if (this.skipNextTickHorizontal) {
                    // Instant move already did horizontal - skip this tick, clear flag
                    this.skipNextTickHorizontal = false;
                } else if (!this.horizontalJustPressed && wantsHorizontal) {
                    const dir = this.inputState.left ? -1 : 1;
                    player.facing = dir;
                    if (this.tryMoveHorizontal(player, dir)) {
                        this.lastHorizontalMoveTime = now;
                        // Send air strafe to other clients for immediate update
                        this.networkClient.sendAirStrafe(dir);
                    }
                }
                
                // Sync unified tick timer to prevent double-move if we land
                this.lastUnifiedMoveTime = now;
                
                // Check if landed
                if (this.isOnGround(player)) {
                    player.state = player.isCrouching ? PlayerState.CROUCHING : PlayerState.IDLE;
                }
            }
            
            // Process pending block placement/pickup AFTER fall (so position is synced)
            // Use CURRENT position (after both player and blocks have fallen together)
            if (player.pendingBlockPlace || player.pendingBlockPickup) {
                player.pendingBlockPlace = false;
                player.pendingBlockPickup = false;
                this.executeDownAction(player, false); // Use current position, not stored
            }
            
            // Also check if player should START falling (ground just disappeared)
            if ((player.state === PlayerState.IDLE || player.state === PlayerState.WALKING || 
                 player.state === PlayerState.CROUCHING) && !this.isOnGround(player)) {
                player.state = PlayerState.FALLING;
                this.tryMoveDown(player);
            }
        }
        
        // Sanity check every frame: if FALLING but on ground, correct state
        // SKIP if pendingLedgeDir is set - we're intentionally about to walk off
        if (player.state === PlayerState.FALLING && this.isOnGround(player) && player.pendingLedgeDir === 0) {
            player.state = player.isCrouching ? PlayerState.CROUCHING : PlayerState.IDLE;
        }
        
        // Safeguard: if RISING but stuck (no Y progress for 300ms), force to FALLING
        // This prevents rare edge cases where player gets stuck mid-air
        if (player.state === PlayerState.RISING) {
            if (player.y < player.lastRisingY) {
                // Made upward progress - reset timer
                player.lastRisingY = player.y;
                player.risingStartTime = now;
            } else if (now - player.risingStartTime > 300) {
                // Stuck for too long - force falling
                console.warn('RISING stuck safeguard triggered', {
                    playerY: player.y,
                    lastRisingY: player.lastRisingY,
                    jumpRemaining: player.jumpRemaining,
                    minJumpRemaining: player.minJumpRemaining,
                    upHeld: this.inputState.up,
                    lastUnifiedMoveTime: this.lastUnifiedMoveTime,
                    timeSinceLastMove: now - this.lastUnifiedMoveTime,
                    isOnGround: this.isOnGround(player),
                    tileAboveHead: this.getTile(player.x, player.y - 1),
                    pendingLedgeDir: player.pendingLedgeDir,
                    horizontalJustPressed: this.horizontalJustPressed,
                    wantsHorizontal: (this.inputState.left && !this.inputState.right) || (this.inputState.right && !this.inputState.left),
                });
                player.state = PlayerState.FALLING;
                player.minJumpRemaining = 0;
            }
        }
        
        // If we were falling this frame (even if we landed), skip other movement
        // This prevents double-moves and state thrashing
        // EXCEPTION: allow instant first horizontal move while falling (but not if pending ledge)
        if (wasFalling && (!this.horizontalJustPressed || player.pendingLedgeDir !== 0)) {
            return;
        }

        // INSTANT FIRST MOVE - horizontal just pressed
        // Rate limited to prevent tap-tap-tap spam, but resets when blocked (standstill)
        // Skip if pendingLedgeDir is set - we're waiting for global tick
        if (player.pendingLedgeDir !== 0) {
            return;
        }
        
        if (this.horizontalJustPressed && wantsHorizontal &&
            now - this.lastHorizontalMoveTime >= MOVE_INTERVAL) {
            const dir = this.inputState.left ? -1 : 1;
            player.facing = dir;
            
            // Check for walk-off-ledge BEFORE trying to move
            // Walk-off-ledge queues move for global tick to sync with fall
            if (this.isWalkOffLedge(player, dir)) {
                player.pendingLedgeDir = dir;
                player.state = PlayerState.FALLING;
                this.lastHorizontalMoveTime = now;
                this.lastUnifiedMoveTime = now;
                return;
            }
            
            // Track if horizontal move causes step-up (vertical change)
            const xBefore = player.x;
            const yBefore = player.y;
            
            // Only update timing if we actually moved (not blocked)
            if (this.tryMoveHorizontal(player, dir)) {
                this.lastHorizontalMoveTime = now;
                // Send air strafe if falling or rising (airborne)
                if (player.state === PlayerState.FALLING || player.state === PlayerState.RISING) {
                    this.networkClient.sendAirStrafe(dir);
                    // Skip next global tick's horizontal - this instant move counts as that tick's move
                    this.skipNextTickHorizontal = true;
                }
            }
            
            const didStepUp = player.y < yBefore;
            
            // If RISING AND tick was ready AND didn't step up, do vertical move
            // Step-up already consumed this tick's vertical movement
            const tickWasReady = now - this.lastUnifiedMoveTime >= MOVE_INTERVAL;
            let didVerticalMove = false;
            if (player.state === PlayerState.RISING && tickWasReady && !didStepUp) {
                this.doVerticalMove(player, now);
                didVerticalMove = true;
            }
            
            // If RISING with step-up, consume jump (diagonal movement)
            if (player.state === PlayerState.RISING && didStepUp) {
                player.jumpRemaining--;
                if (player.minJumpRemaining > 0) player.minJumpRemaining--;
            }
            
            // If FALLING and now on ground (step-up landed), update state
            if (player.state === PlayerState.FALLING && this.isOnGround(player)) {
                player.state = player.isCrouching ? PlayerState.CROUCHING : PlayerState.IDLE;
            }
            
            // Only reset unified tick timer if we actually did movement
            // Otherwise spamming blocked horizontal during RISING would prevent vertical moves
            const didAnyMovement = (player.x !== xBefore) || (player.y !== yBefore) || didVerticalMove;
            if (didAnyMovement) {
                this.lastUnifiedMoveTime = now;
            }
            
            // Check if we walked off a ledge (will set state but not move)
            this.checkFallOffLedge(player);
            return;
        }

        // UNIFIED TICK - for held/continuous movement (when NOT falling)
        if (player.state === PlayerState.FALLING) return;
        
        const tickReady = now - this.lastUnifiedMoveTime >= MOVE_INTERVAL;
        if (!tickReady) return;

        this.lastUnifiedMoveTime = now;

        // Try horizontal FIRST - step-up takes priority over rise
        // This prevents +2 vertical in one tick (rise + step-up)
        let didStepUp = false;
        if (wantsHorizontal) {
            const dir = this.inputState.left ? -1 : 1;
            player.facing = dir;
            
            // Check for walk-off-ledge - queue for global tick
            if (this.isWalkOffLedge(player, dir)) {
                player.pendingLedgeDir = dir;
                player.state = PlayerState.FALLING;
                this.lastHorizontalMoveTime = now;
                return; // Skip rest - will process on global tick
            }
            
            const yBefore = player.y;
            if (this.tryMoveHorizontal(player, dir)) {
                this.lastHorizontalMoveTime = now;
                didStepUp = player.y < yBefore;
            }
        }

        // Vertical movement - skip if we already stepped up
        if (!didStepUp) {
            this.doVerticalMove(player, now);
        } else if (player.state === PlayerState.RISING) {
            // Step-up during RISING is diagonal movement - consume jump and keep rising
            player.jumpRemaining--;
            if (player.minJumpRemaining > 0) player.minJumpRemaining--;
            // State stays RISING - next tick doVerticalMove will check if arc is done
        }

        // Check if we walked off a ledge
        this.checkFallOffLedge(player);
    }

    private doVerticalMove(player: LocalPlayerState, now: number): void {
        // Rising - uses unified tick timing (instant-first)
        if (player.state === PlayerState.RISING) {
            const mustJump = player.minJumpRemaining > 0;
            const wantsJump = this.inputState.up && player.jumpRemaining > 0;

            if (mustJump || wantsJump) {
                if (this.tryMoveUp(player)) {
                    player.jumpRemaining--;
                    if (player.minJumpRemaining > 0) player.minJumpRemaining--;
                } else {
                    player.minJumpRemaining = 0;
                    player.state = PlayerState.FALLING;
                }
            } else {
                player.state = PlayerState.FALLING;
            }
            return;
        }

        // Falling movement is now handled by global-aligned tick (above in updateLocalPlayer)
        // Just handle state transitions here
        if (!this.isOnGround(player) && player.state !== PlayerState.FALLING) {
            player.state = PlayerState.FALLING;
        } else if (this.isOnGround(player) && player.state === PlayerState.FALLING) {
            player.state = player.isCrouching ? PlayerState.CROUCHING : PlayerState.IDLE;
        }

        // Update crouching state
        if (player.isCrouching && this.isOnGround(player)) {
            player.state = PlayerState.CROUCHING;
        }
    }

    private checkFallOffLedge(player: LocalPlayerState): void {
        // If we're supposedly grounded but actually have no floor
        if (player.state === PlayerState.IDLE || player.state === PlayerState.WALKING ||
            player.state === PlayerState.CROUCHING) {
            if (!this.isOnGround(player)) {
                // Try to step down 1 tile
                this.tryMoveDown(player);
                
                // Did we land? (1-tile step down)
                if (this.isOnGround(player)) {
                    // Landed - stay in normal state, diagonal step complete
                    player.state = player.isCrouching ? PlayerState.CROUCHING : PlayerState.IDLE;
                } else {
                    // Still no ground - start falling, wait for global tick
                    player.state = PlayerState.FALLING;
                }
            }
        }
    }

    private handleDownAction(player: LocalPlayerState): void {
        // If falling, queue block action for global tick to sync with block falls
        if (player.state === PlayerState.FALLING) {
            const frontX = player.x + player.facing;
            const playerHeight = player.isCrouching ? 1 : 2;
            const feetY = player.y + playerHeight - 1;
            
            // Capture position at input time
            player.pendingActionX = frontX;
            player.pendingActionY = feetY;
            
            if (player.isHoldingBlock && player.heldBlockType !== null) {
                player.pendingBlockPlace = true;
            } else {
                player.pendingBlockPickup = true;
            }
            return;
        }
        
        this.executeDownAction(player);
    }
    
    // Execute the actual down action (place/pickup/crouch)
    // Called directly when grounded or for immediate actions
    // If useStoredPos is true, uses pendingActionX/Y instead of calculating from current position
    private executeDownAction(player: LocalPlayerState, useStoredPos: boolean = false): void {
        let frontX: number;
        let feetY: number;
        
        if (useStoredPos) {
            frontX = player.pendingActionX;
            feetY = player.pendingActionY;
        } else {
            frontX = player.x + player.facing;
            const playerHeight = player.isCrouching ? 1 : 2;
            feetY = player.y + playerHeight - 1;
        }

        const frontBottomTile = this.getTile(frontX, feetY);
        const frontTopTile = this.getTile(frontX, feetY - 1);

        if (player.isHoldingBlock && player.heldBlockType !== null) {
            if (frontBottomTile === TileType.SKY && frontTopTile === TileType.SKY) {
                this.setLocalTile(frontX, feetY, player.heldBlockType);
                // Send NEW state: not holding after place
                this.networkClient.sendBlockAction(
                    'place', frontX, feetY, player.heldBlockType,
                    player.x, player.y, player.state, player.facing,
                    player.isCrouching, false, // NEW state: not holding
                    -1 // NEW state: no held block type
                );
                // DON'T add to falling list here - wait for server broadcast
                // This ensures all clients start falling at the same time
                player.isHoldingBlock = false;
                player.heldBlockType = null;
                return;
            } else if (frontTopTile === TileType.SKY && frontBottomTile !== TileType.SKY) {
                this.setLocalTile(frontX, feetY - 1, player.heldBlockType);
                // Send NEW state: not holding after place
                this.networkClient.sendBlockAction(
                    'place', frontX, feetY - 1, player.heldBlockType,
                    player.x, player.y, player.state, player.facing,
                    player.isCrouching, false, // NEW state: not holding
                    -1 // NEW state: no held block type
                );
                // DON'T add to falling list here - wait for server broadcast
                player.isHoldingBlock = false;
                player.heldBlockType = null;
                return;
            }
        } else {
            // Can pick up dirt or grass blocks
            if (this.isFallingType(frontBottomTile) && frontTopTile === TileType.SKY) {
                const pickedUpType = frontBottomTile;
                this.setLocalTile(frontX, feetY, TileType.SKY);
                // Send NEW state: holding after pickup
                this.networkClient.sendBlockAction(
                    'pickup', frontX, feetY, pickedUpType,
                    player.x, player.y, player.state, player.facing,
                    player.isCrouching, true, // NEW state: holding
                    pickedUpType // NEW state: held block type
                );
                // Remove from falling tracking if it was there
                this.localFallingBlocks.delete(`${frontX},${feetY}`);
                player.isHoldingBlock = true;
                player.heldBlockType = pickedUpType;
                return;
            }
        }

        if (this.isOnGround(player) && !player.isCrouching) {
            player.y += 1;
            player.isCrouching = true;
            player.state = PlayerState.CROUCHING;
        }
    }

    private setLocalTile(x: number, y: number, type: TileType): void {
        const cx = Math.floor(x / CHUNK_SIZE);
        const cy = Math.floor(y / CHUNK_SIZE);
        const key = `${cx},${cy}`;
        const chunk = this.chunks.get(key);
        if (chunk) {
            const localX = ((x % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
            const localY = ((y % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
            chunk[localY][localX] = type;
        }
    }

    // Client-side falling block prediction
    // Called on same global tick as player fall to keep them synced
    private tickLocalFallingBlocks(): void {
        const toRemove: string[] = [];
        const toAdd: string[] = [];
        
        // Sort by Y descending (bottom blocks first) for correct stacking
        const sortedBlocks = Array.from(this.localFallingBlocks)
            .map(key => {
                const [x, y] = key.split(',').map(Number);
                return { key, x, y };
            })
            .sort((a, b) => b.y - a.y);
        
        for (const { key, x, y } of sortedBlocks) {
            // Verify block is still a falling type (dirt or grass)
            const tile = this.getTile(x, y);
            if (!this.isFallingType(tile)) {
                toRemove.push(key);
                continue;
            }
            
            // Check if can fall
            const belowTile = this.getTile(x, y + 1);
            const belowKey = `${x},${y + 1}`;
            
            if (belowTile === TileType.SKY) {
                // Fall one tile - preserve original tile type
                this.setLocalTile(x, y, TileType.SKY);
                this.setLocalTile(x, y + 1, tile);
                toRemove.push(key);
                toAdd.push(`${x},${y + 1}`);
                
                // UPWARD PROPAGATION: check if there's a falling type above that should now fall
                const aboveY = y - 1;
                const aboveTile = this.getTile(x, aboveY);
                if (this.isFallingType(aboveTile)) {
                    const aboveKey = `${x},${aboveY}`;
                    if (!this.localFallingBlocks.has(aboveKey) && !toAdd.includes(aboveKey)) {
                        toAdd.push(aboveKey);
                    }
                }
            } else if (this.isFallingType(belowTile)) {
                // Resting on another falling type - check if that block is also falling
                const belowAlsoFalling = this.localFallingBlocks.has(belowKey) || toAdd.includes(belowKey);
                if (belowAlsoFalling) {
                    // Stay in falling list - we'll fall together next tick
                    // Don't remove, don't add (already in list)
                } else {
                    // Below block is stable - we've landed
                    toRemove.push(key);
                }
            } else {
                // Landed on stone or other solid - stop tracking
                toRemove.push(key);
            }
        }
        
        // Update the set
        for (const key of toRemove) {
            this.localFallingBlocks.delete(key);
        }
        for (const key of toAdd) {
            this.localFallingBlocks.add(key);
        }
    }

    // ============================================================
    // PHYSICS HELPERS
    // ============================================================

    private isOnGround(player: LocalPlayerState): boolean {
        const h = player.isCrouching ? 1 : 2;
        return this.getTile(player.x, player.y + h) !== TileType.SKY;
    }

    private tryMoveHorizontal(player: LocalPlayerState, dir: number): boolean {
        const newX = player.x + dir;
        const h = player.isCrouching ? 1 : 2;
        const feetY = player.y + h - 1;

        // Check if body space is clear at destination
        let canFitFlat = true;
        for (let y = 0; y < h; y++) {
            if (this.getTile(newX, player.y + y) !== TileType.SKY) {
                canFitFlat = false;
                break;
            }
        }
        
        if (canFitFlat) {
            // Body fits at same Y level - but check ground situation
            const hasGroundFlat = this.getTile(newX, player.y + h) !== TileType.SKY;
            const hasGroundStepDown = this.getTile(newX, player.y + h + 1) !== TileType.SKY;
            const canFitStepDown = this.getTile(newX, player.y + h) === TileType.SKY; // Space for body at y+1
            
            // Check if player is in a grounded STATE (not just physically on ground)
            // RISING/FALLING are "in air" even if momentarily touching ground
            const inGroundedState = player.state === PlayerState.IDLE || 
                                    player.state === PlayerState.WALKING || 
                                    player.state === PlayerState.CROUCHING;
            const currentlyGrounded = inGroundedState && this.isOnGround(player);
            
            if (hasGroundFlat) {
                // Ground at same level - flat move
                player.x = newX;
                return true;
            } else if (currentlyGrounded && canFitStepDown && hasGroundStepDown) {
                // No ground at same level, but ground at step-down - do diagonal step-down
                player.x = newX;
                player.y = player.y + 1;
                return true;
            } else if (!currentlyGrounded) {
                // In air state (RISING/FALLING) or not on ground - flat move is fine
                player.x = newX;
                return true;
            } else {
                // Walk-off-ledge: grounded state but no ground at destination
                // Return false - caller should handle via global fall tick
                return false;
            }
        }

        // Can't fit flat - try step up
        if (this.getTile(newX, feetY) !== TileType.SKY) {
            let canStep = true;
            const newY = player.y - 1;
            for (let y = 0; y < h; y++) {
                if (this.getTile(newX, newY + y) !== TileType.SKY) {
                    canStep = false;
                    break;
                }
            }
            if (canStep) {
                player.x = newX;
                player.y = newY;
                return true;
            }
        }

        return false;
    }

    private tryMoveUp(player: LocalPlayerState): boolean {
        if (this.getTile(player.x, player.y - 1) !== TileType.SKY) return false;
        player.y--;
        return true;
    }

    private tryMoveDown(player: LocalPlayerState): boolean {
        const h = player.isCrouching ? 1 : 2;
        if (this.getTile(player.x, player.y + h) !== TileType.SKY) return false;
        player.y++;
        return true;
    }

    // Check if moving in direction would result in walk-off-ledge
    // (body fits but no ground at destination or step-down)
    // Only applies to grounded states - RISING/FALLING handle ledges differently
    private isWalkOffLedge(player: LocalPlayerState, dir: number): boolean {
        // Only check for grounded states - RISING and FALLING don't use this
        if (player.state !== PlayerState.IDLE && 
            player.state !== PlayerState.WALKING && 
            player.state !== PlayerState.CROUCHING) {
            return false;
        }
        
        if (!this.isOnGround(player)) return false; // Already not grounded
        
        const newX = player.x + dir;
        const h = player.isCrouching ? 1 : 2;
        
        // Check if body space is clear at destination
        for (let y = 0; y < h; y++) {
            if (this.getTile(newX, player.y + y) !== TileType.SKY) {
                return false; // Blocked, not walk-off-ledge
            }
        }
        
        // Body fits - check ground
        const hasGroundFlat = this.getTile(newX, player.y + h) !== TileType.SKY;
        const hasGroundStepDown = this.getTile(newX, player.y + h + 1) !== TileType.SKY;
        
        // Walk-off-ledge if no ground at flat OR step-down
        return !hasGroundFlat && !hasGroundStepDown;
    }

    private tryEndCrouch(player: LocalPlayerState): boolean {
        if (!player.isCrouching) return false;
        if (this.getTile(player.x, player.y - 1) === TileType.SKY) {
            player.y--;
            player.isCrouching = false;
            player.state = this.isOnGround(player) ? PlayerState.IDLE : PlayerState.FALLING;
            return true;
        }
        return false;
    }

    private getTile(x: number, y: number): TileType {
        const cx = Math.floor(x / CHUNK_SIZE);
        const cy = Math.floor(y / CHUNK_SIZE);
        const chunk = this.chunks.get(`${cx},${cy}`);
        if (!chunk) return TileType.SKY;
        const lx = ((x % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
        const ly = ((y % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
        return chunk[ly]?.[lx] ?? TileType.SKY;
    }

    // ============================================================
    // NETWORK
    // ============================================================

    private sendPositionUpdate(now: number): void {
        if (!this.localPlayer) return;
        if (now - this.lastPositionSendTime < 33) return;
        this.lastPositionSendTime = now;

        const p = this.localPlayer;
        this.networkClient.sendPosition(
            p.x, p.y, p.state, p.facing,
            p.isCrouching, p.isHoldingBlock, p.heldBlockType
        );
    }

    private updateChunkSubscriptions(): void {
        if (!this.localPlayer) return;

        const halfChunks = Math.floor(CHUNKS_LOADED / 2);
        const centerCx = Math.floor(this.localPlayer.x / CHUNK_SIZE);
        const centerCy = Math.floor(this.localPlayer.y / CHUNK_SIZE);

        // Only update if player moved to a different chunk center
        const centerKey = `${centerCx},${centerCy}`;
        if (centerKey === this.lastChunkCenter) return;
        this.lastChunkCenter = centerKey;

        const allNeeded: { cx: number; cy: number }[] = [];
        const neededKeys = new Set<string>();

        for (let cy = centerCy - halfChunks; cy <= centerCy + halfChunks; cy++) {
            for (let cx = centerCx - halfChunks; cx <= centerCx + halfChunks; cx++) {
                const key = `${cx},${cy}`;
                neededKeys.add(key);
                allNeeded.push({ cx, cy });
            }
        }

        // Send full list so server knows all subscribed chunks
        this.networkClient.subscribeToChunks(allNeeded);
        this.subscribedChunks = neededKeys;
    }

    // ============================================================
    // RENDERING
    // ============================================================

    private render(now: number): void {
        if (!this.localPlayer) return;

        const lp = this.localPlayer;

        // Camera offset
        const screenCenterX = this.cameras.main.width / 2;
        const screenCenterY = this.cameras.main.height / 2;
        const playerScreenX = screenCenterX - TILE_SIZE / 2;
        const playerScreenY = screenCenterY - TILE_SIZE; // Top of 2-tile player
        const offsetX = playerScreenX - lp.x * TILE_SIZE;
        const offsetY = playerScreenY - lp.y * TILE_SIZE;

        // Tiles - clear previous frame's active tiles
        const previousTileKeys = this.activeTileKeys;
        this.activeTileKeys = new Set();
        
        const halfChunks = Math.floor(CHUNKS_LOADED / 2);
        const centerCx = Math.floor(lp.x / CHUNK_SIZE);
        const centerCy = Math.floor(lp.y / CHUNK_SIZE);

        for (let cy = centerCy - halfChunks; cy <= centerCy + halfChunks; cy++) {
            for (let cx = centerCx - halfChunks; cx <= centerCx + halfChunks; cx++) {
                const chunk = this.chunks.get(`${cx},${cy}`);
                if (!chunk) continue;

                for (let ly = 0; ly < CHUNK_SIZE; ly++) {
                    for (let lx = 0; lx < CHUNK_SIZE; lx++) {
                        const worldX = cx * CHUNK_SIZE + lx;
                        const worldY = cy * CHUNK_SIZE + ly;
                        const tile = chunk[ly]?.[lx] ?? TileType.SKY;
                        
                        // Skip SKY tiles - background color handles them
                        if (tile === TileType.SKY) continue;
                        
                        const sx = worldX * TILE_SIZE + offsetX;
                        const sy = worldY * TILE_SIZE + offsetY;
                        
                        // Get texture name for tile type
                        const textureName = this.getTileTexture(tile as TileType);
                        const tileKey = `${worldX},${worldY}`;
                        
                        this.activeTileKeys.add(tileKey);
                        
                        let tileSprite = this.tileSprites.get(tileKey);
                        if (!tileSprite) {
                            tileSprite = this.add.image(sx, sy, textureName);
                            tileSprite.setOrigin(0, 0);
                            tileSprite.setDepth(10);
                            this.tileSprites.set(tileKey, tileSprite);
                        } else {
                            tileSprite.setTexture(textureName);
                            tileSprite.setPosition(sx, sy);
                            tileSprite.setVisible(true);
                        }
                    }
                }
            }
        }
        
        // Hide tiles that are no longer visible
        for (const key of previousTileKeys) {
            if (!this.activeTileKeys.has(key)) {
                const sprite = this.tileSprites.get(key);
                if (sprite) {
                    sprite.setVisible(false);
                }
            }
        }

        // Other players (interpolated)
        this.otherPlayers.forEach((queue, id) => {
            const snap = queue.getInterpolatedPosition(now);
            if (!snap) return;

            // Get player-specific hue-shifted textures
            const playerTextures = this.getPlayerTextures(id);
            
            let sprite = this.otherPlayerSprites.get(id);
            if (!sprite) {
                sprite = this.add.sprite(0, 0, playerTextures.man_right);
                sprite.setOrigin(0, 0);
                sprite.setScale(1);
                sprite.setDepth(50);
                // No tint - using hue-shifted textures instead
                this.otherPlayerSprites.set(id, sprite);
            }

            const sx = snap.x * TILE_SIZE + offsetX;
            const sy = snap.y * TILE_SIZE + offsetY;

            this.updatePlayerSprite(sprite, snap.state, snap.facing, snap.isCrouching, playerTextures);
            
            // Crouching: draw 1 tile higher, depth behind tiles
            if (snap.isCrouching) {
                sprite.setPosition(sx, sy - TILE_SIZE);
                sprite.setDepth(5);
            } else {
                sprite.setPosition(sx, sy);
                sprite.setDepth(50);
            }
            sprite.setVisible(true);
            
            // Held block for other players
            let heldBlockSprite = this.otherPlayerHeldBlocks.get(id);
            if (snap.isHoldingBlock && snap.heldBlockType >= 0) {
                const textureName = this.getTileTexture(snap.heldBlockType as TileType);
                if (!heldBlockSprite) {
                    heldBlockSprite = this.add.image(0, 0, textureName);
                    heldBlockSprite.setOrigin(0, 0);
                    heldBlockSprite.setDepth(55); // Above other player sprites
                    this.otherPlayerHeldBlocks.set(id, heldBlockSprite);
                }
                // Use same sprite Y position as the player sprite
                const spriteY = snap.isCrouching ? sy - TILE_SIZE : sy;
                const blockY = spriteY - TILE_SIZE + 6; // Same offset as local player
                heldBlockSprite.setTexture(textureName);
                heldBlockSprite.setPosition(sx, blockY);
                heldBlockSprite.setVisible(true);
            } else if (heldBlockSprite) {
                heldBlockSprite.setVisible(false);
            }
        });

        // Remove sprites for disconnected players
        for (const [id, sprite] of this.otherPlayerSprites) {
            if (!this.otherPlayers.has(id)) {
                sprite.destroy();
                this.otherPlayerSprites.delete(id);
                // Also clean up held block sprite
                const heldBlockSprite = this.otherPlayerHeldBlocks.get(id);
                if (heldBlockSprite) {
                    heldBlockSprite.destroy();
                    this.otherPlayerHeldBlocks.delete(id);
                }
                // Clean up player-specific textures
                const playerTextures = this.playerTextureCache.get(id);
                if (playerTextures) {
                    this.textures.remove(playerTextures.man_right);
                    this.textures.remove(playerTextures.squat_man_right);
                    this.textures.remove(playerTextures.leap_man_right);
                    this.textures.remove(playerTextures.fall_man_right);
                    this.playerTextureCache.delete(id);
                }
            }
        }

        // Local player sprite
        this.updatePlayerSprite(this.localPlayerSprite, lp.state, lp.facing, lp.isCrouching);
        
        // Crouching: draw 1 tile higher (sprite is still 2 tiles tall, but player.y shifted down)
        // Also set depth behind tiles when crouching
        if (lp.isCrouching) {
            this.localPlayerSprite.setPosition(playerScreenX, playerScreenY - TILE_SIZE);
            this.localPlayerSprite.setDepth(5); // Behind tiles
        } else {
            this.localPlayerSprite.setPosition(playerScreenX, playerScreenY);
            this.localPlayerSprite.setDepth(100); // In front of tiles
        }
        this.localPlayerSprite.clearTint(); // No tint for local player

        // Held block
        if (lp.isHoldingBlock && lp.heldBlockType !== null) {
            // Use same sprite Y position as the player sprite
            const spriteY = lp.isCrouching ? playerScreenY - TILE_SIZE : playerScreenY;
            const blockY = spriteY - TILE_SIZE + 6; // 7/8 overlapping player sprite
            const textureName = this.getTileTexture(lp.heldBlockType);
            this.localHeldBlockSprite.setTexture(textureName);
            this.localHeldBlockSprite.setPosition(playerScreenX, blockY);
            this.localHeldBlockSprite.setVisible(true);
        } else {
            this.localHeldBlockSprite.setVisible(false);
        }
    }

    private updatePlayerSprite(
        sprite: Phaser.GameObjects.Sprite,
        state: PlayerState,
        facing: number,
        isCrouching: boolean,
        playerTextures?: {
            man_right: string;
            squat_man_right: string;
            leap_man_right: string;
            fall_man_right: string;
        }
    ): void {
        // Use player-specific textures if provided, otherwise use default
        const textures = playerTextures || this.processedTextures;
        
        // Choose texture based on state
        let texture = textures.man_right;

        if (isCrouching || state === PlayerState.JUMPSQUAT || state === PlayerState.CROUCHING) {
            texture = textures.squat_man_right;
        } else if (state === PlayerState.RISING) {
            texture = textures.leap_man_right;
        } else if (state === PlayerState.FALLING) {
            texture = textures.fall_man_right;
        }

        sprite.setTexture(texture);

        // Flip for left facing
        sprite.setFlipX(facing === -1);
    }
    
    // Get the processed texture key for a tile type
    private getTileTexture(tileType: TileType): string {
        switch (tileType) {
            case TileType.DIRT:
                return this.processedTextures.dirt;
            case TileType.GRASS:
                return this.processedTextures.grass;
            case TileType.STONE:
            default:
                return this.processedTextures.stone;
        }
    }
    
    // Check if a tile type is affected by gravity (can fall)
    private isFallingType(tileType: TileType): boolean {
        return tileType === TileType.DIRT || tileType === TileType.GRASS;
    }
}
